import * as React from 'react';
import cx from 'classnames';
import { ImageFieldProps, useImageFieldController } from './image-field.controller';
import { DatasheetItemLabel } from '@modules/datasheets/components/datasheet-item-label';
import { DatasheetItem } from '@modules/datasheets/components/datasheet-item';
import { getHeadingAlignmentStyle, getHeadingPositionOffset } from '../field-heading.utils';
import { Flex } from '@common/components/Flex';
import { FieldContainer } from '../field-container';
import { ImageEditor } from '@common/components/image/ImageEditor';
import { UploadModal } from '@common/components/upload-modal/UploadModal';
import { MessageBox } from '@common/components/MessageBox';
import { useDatasheetNavigationContext, useDatasheetResultFieldEditable, useForceDisabled } from '../../context';

import './image-field.styles.scss';

export const ImageField: React.FC<ImageFieldProps> = (props) => {
  const { datasheetElement, context, globalId } = props;

  const {
    fieldCurrentValue,
    handleFieldValueChange,
    handleFieldValueSubmit,
    handleFieldValueBlur,
    handleUploadClick,
    showUploadModal,
    setShowUploadModal,
    handleFileUpload,
    headingPlacedHorizontally, 
    headingReference, 
    headingSize, 
    headingPosition,
    messageProps,
    closeMessage,
  } = useImageFieldController(props);

  const isEditable = useDatasheetResultFieldEditable(Boolean(datasheetElement.original.editable));
  const forceDisabled = useForceDisabled();
  const showBorder = datasheetElement.original.showborder !== false;
  const [isEditing, setIsEditing] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const fieldContainerRef = React.useRef<HTMLDivElement>(null);

  const { registerField, focusNext, focusPrev } = useDatasheetNavigationContext();

  React.useEffect(() => {
    if (!isEditable) return;
    registerField(datasheetElement.id, {
      focus: () => {
        const input = fieldContainerRef.current?.querySelector<HTMLInputElement>('input');
        input?.focus();
      },
    });
    return () => { registerField(datasheetElement.id, null); };
  }, [datasheetElement.id, registerField, isEditable]);

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key !== 'Tab' || !isEditable) return;
      e.preventDefault();
      if (e.shiftKey) {
        focusPrev(datasheetElement.id);
      } else {
        focusNext(datasheetElement.id);
      }
    },
    [datasheetElement.id, focusNext, focusPrev, isEditable],
  );

  // Construct image preview URL (matches spreadsheet format: api/documents/open/...)
  // This endpoint handles cookies automatically, no need to manually add XSRF cookie
  const imagePreviewUrl = React.useMemo(() => {
    if (forceDisabled) return null;
    if (!fieldCurrentValue || fieldCurrentValue.trim() === '') return null;
    if (!globalId || !context || !datasheetElement.original.field.fieldid) return null;
    
    const field = datasheetElement.original.field;
    const fieldId = field.fieldid;
    const tableId = field.table ? field.fieldid : 0;
    const rrn = field.table ? 1 : -1;
    
    // Convert fieldcontext string to number if needed
    let fieldContext = context;
    if (field.fieldcontext) {
      const contextStr = String(field.fieldcontext);
      if (contextStr === 'IDS_CONTEXT' || contextStr === '1') {
        fieldContext = 1;
      } else if (contextStr === 'PDS_CONTEXT' || contextStr === '2') {
        fieldContext = 2;
      } else if (contextStr === '8' || contextStr === 'TABLE_CONTEXT') {
        fieldContext = 8;
      } else {
        const parsed = parseInt(contextStr, 10);
        if (!isNaN(parsed)) {
          fieldContext = parsed;
        }
      }
    }
    
    const params = new URLSearchParams({
      fieldId: String(fieldId),
      fieldContext: String(fieldContext),
      objectId: String(globalId),
      tableId: String(tableId),
      rrn: String(rrn),
    });
    
    return `api/documents/open/${encodeURIComponent(fieldCurrentValue)}?${params.toString()}`;
  }, [forceDisabled, fieldCurrentValue, globalId, context, datasheetElement.original.field]);

  // Handle click outside to hide editor and show image preview
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isEditing && containerRef.current && !containerRef.current.contains(event.target as Node)) {
        // Before hiding editor, trigger blur handler to save the value (if changed)
        // This ensures API call is made when clicking outside
        if (isEditable && fieldCurrentValue) {
          handleFieldValueBlur(fieldCurrentValue);
        }
        // Hide editor after a short delay to allow API call to complete
        setTimeout(() => {
          setIsEditing(false);
        }, 100);
      }
    };

    if (isEditing) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isEditing, isEditable, fieldCurrentValue, handleFieldValueBlur]);

  const fieldWidth = datasheetElement.original.width;
  const fieldHeight = datasheetElement.original.height;

  const headingAlignmentStyle = getHeadingAlignmentStyle({
    headingPosition,
    headAlign: datasheetElement.original.headalign,
    fieldWidth,
  });

  const headingPositionOffset = getHeadingPositionOffset({
    headingPosition,
    headAlign: datasheetElement.original.headalign,
    headingSize,
    fieldWidth,
    fieldHeight,
  });

  const fieldIsEmpty = (() => { const v = (datasheetElement.original as any).field?.name ?? (datasheetElement.original as any).field?.value; return v == null || String(v).trim() === ''; })();

  const labelComponent = (
    <DatasheetItemLabel
      reference={headingReference}
      inline={headingPlacedHorizontally}
      withImg={datasheetElement.original.showfieldtype}
      withText={datasheetElement.original.headdisplay}
      text={datasheetElement.original.headtext}
      img={datasheetElement.original.field.imageURL}
      textCustomization={{
        fontSize: datasheetElement.original.headfontsize,
        fontFamily: datasheetElement.original.headfont,
        color: datasheetElement.original.headcolor,
        align: datasheetElement.original.headalign,
        fontStyles: datasheetElement.original.headstyle,
      }}
      absolutePosition={true}
      alignmentStyle={headingAlignmentStyle}
      positionOffset={headingPositionOffset}
    />
  );

  return (
    <>
      <MessageBox hideMessageBox={closeMessage} {...messageProps} />
      <DatasheetItem
        style={{
          top: datasheetElement.coordinates.y,
          left: datasheetElement.coordinates.x,
        }}
      >
        {labelComponent}
        <Flex alignItems="center" style={{ position: 'relative' }}>
          <FieldContainer
            ref={fieldContainerRef}
            fieldId={datasheetElement.original.field.fieldid ?? datasheetElement.original.field.id}
            onKeyDown={handleKeyDown}
            showBorder={datasheetElement.original.showborder}
            width={fieldWidth}
            height={fieldHeight}
            className={!isEditable ? 'DatasheetsResultImageField__field-container--disabled' : ''}
            required={datasheetElement.original.required}
            isEmpty={fieldIsEmpty}
          >
            <div 
              ref={containerRef}
              className="DatasheetsResultImageField__container"
              onClick={() => {
                if (isEditable && !isEditing && imagePreviewUrl) {
                  setIsEditing(true);
                }
              }}
            >
              {/* ImageEditor overlay - shows at top (28px) when editing, sticky at top, overlays on image preview */}
              {isEditing && (
                <div
                  className={cx(
                    "DatasheetsResultImageField__editor-overlay",
                    { "DatasheetsResultImageField__editor-overlay--no-border": !showBorder }
                  )}
                >
                  <ImageEditor
                    value={fieldCurrentValue}
                    onChange={(value: string) => {
                      if (isEditable) {
                        handleFieldValueChange(value);
                        setIsEditing(true);
                      }
                    }}
                    onUpload={isEditable ? handleUploadClick : () => {}}
                    onSubmit={(value: string) => {
                      if (isEditable) {
                        handleFieldValueSubmit(value);
                        setIsEditing(false);
                      }
                    }}
                    onBlur={(value: string) => {
                      if (isEditable) {
                        // Get the current value from input (value param) or fallback to state
                        const currentInputValue = value !== undefined ? value : fieldCurrentValue;
                        
                        // Always call blur handler - updateDatasheetElement will check if value changed
                        // This ensures the API is called when the value actually changed
                        handleFieldValueBlur(currentInputValue);
                        // Hide editor after a short delay to allow API call to complete
                        setTimeout(() => {
                          setIsEditing(false);
                        }, 100);
                      }
                    }}
                    disabled={!isEditable}
                    className={cx(
                      "DatasheetsResultImageField__editor",
                      { "DatasheetsResultImageField__editor--no-border": !showBorder }
                    )}
                  />
                </div>
              )}
              
              {/* Image preview - always show when has value */}
              {imagePreviewUrl && (
                <img
                  key={imagePreviewUrl}
                  src={imagePreviewUrl}
                  alt={fieldCurrentValue}
                  className="DatasheetsResultImageField__preview"
                  onError={(e) => {
                    // If image fails to load, hide the image
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                  }}
                />
              )}
              
              {/* Show editor when no image preview available */}
              {!isEditing && !imagePreviewUrl ? (
                /* Show editor when no image preview available */
                <div
                  className={cx(
                    "DatasheetsResultImageField__editor-overlay",
                    { "DatasheetsResultImageField__editor-overlay--no-border": !showBorder }
                  )}
                >
                  <ImageEditor
                    value={fieldCurrentValue}
                    onChange={(value: string) => {
                      if (isEditable) {
                        handleFieldValueChange(value);
                        setIsEditing(true);
                      }
                    }}
                    onUpload={isEditable ? handleUploadClick : () => {}}
                    onSubmit={(value: string) => {
                      if (isEditable) {
                        handleFieldValueSubmit(value);
                        setIsEditing(false);
                      }
                    }}
                    onBlur={(value: string) => {
                      if (isEditable) {
                        // Get the current value from input (value param) or fallback to state
                        const currentInputValue = value !== undefined ? value : fieldCurrentValue;
                        
                        // Always call blur handler - updateDatasheetElement will check if value changed
                        // This ensures the API is called when the value actually changed
                        handleFieldValueBlur(currentInputValue);
                        // Hide editor after a short delay to allow API call to complete
                        setTimeout(() => {
                          setIsEditing(false);
                        }, 100);
                      }
                    }}
                    disabled={!isEditable}
                    className={cx(
                      "DatasheetsResultImageField__editor",
                      { "DatasheetsResultImageField__editor--no-border": !showBorder }
                    )}
                  />
                </div>
              ) : null}
            </div>
          </FieldContainer>
        </Flex>
      </DatasheetItem>
      <UploadModal
        isOpen={showUploadModal && isEditable}
        onClose={() => {
          setShowUploadModal(false);
          setIsEditing(false);
        }}
        onUpload={isEditable ? async (file: File) => {
          await handleFileUpload(file);
          setIsEditing(false);
        } : () => {}}
        title="Upload Image"
        accept={['.jpg', '.jpeg', '.png', '.gif', '.bmp']}
      />
    </>
  );
};

